import type { SignPathsApiArg, SignPathsApiResponse, UploadFileApiArg } from '@/__generated/es-api'
import type { FileUploadType } from '@/app/types'
import config from '@/config'

export type CampaignFileUploadApiArg = Omit<UploadFileApiArg, 'filter'> & {
  filter?: FileUploadType['filter']
}

export const getCampaignFileDownloadPath = ({ orgId, filename }: { orgId: number; filename: string }) =>
  `/v1/organizations/${orgId}/files/${filename}`

export const getCampaignFileSignPathsApiArg = (signedPath: string): SignPathsApiArg => ({
  body: {
    paths: [signedPath],
  },
})

export const getCampaignFileSignRequestKey = ({ orgId, filename }: { orgId: number; filename: string }) =>
  `${orgId}-${filename}`

export const getSignedCampaignFileUrl = ({ signedPath, urlToken }: { signedPath: string; urlToken: string }) =>
  `${config.svc.url}${signedPath}?token=${urlToken}`

// Multiple messages or hook instances can request the same organization's file at once.
// Share its in-flight signing request; remove it when settled so later calls get a fresh token.
const pendingCampaignFileSignRequests = new Map<string, Promise<SignPathsApiResponse>>()

export const getPendingCampaignFileSignPaths = (key: string, request: () => Promise<SignPathsApiResponse>) => {
  const pendingRequest = pendingCampaignFileSignRequests.get(key)
  if (pendingRequest) {
    return pendingRequest
  }

  const nextRequest = request().finally(() => {
    pendingCampaignFileSignRequests.delete(key)
  })
  pendingCampaignFileSignRequests.set(key, nextRequest)
  return nextRequest
}

export const getCampaignFileUploadApiArg = ({
  item,
  orgId,
}: {
  item: FileUploadType
  orgId: number
}): CampaignFileUploadApiArg => ({
  orgId: orgId.toString(),
  category: 'uploads',
  filter: item.filter,
  video: item.type === 'video' ? true : undefined,
  body: {
    upload: [getCampaignFileUploadFile(item)],
  },
})

const getCampaignFileUploadFile = ({ file, filename }: FileUploadType) => {
  if (file.name === filename) {
    return file
  }

  return new File([file], filename, {
    lastModified: file.lastModified,
    type: file.type,
  })
}
